/**
 * Assign / Change Bike, from the rider.
 *
 * The same register, assignments and ownership history as the Bike Register:
 * nothing here is a second ownership system. The rider's bike shows who owns
 * it (Self, Company or Investor) with the ownership panel the bike itself
 * uses; another bike on the register can be handed to them from a date; or a
 * new bike registered straight into their hands, its owner chosen up front.
 */

import { useState, type ReactNode } from 'react';
import type { BikeOwnership, RiderDirectoryRow } from '@cenox/shared';
import { Button, ErrorState, Input, Modal, Select, TableSkeleton } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { todayIn } from '../../lib/format';
import { BikeOwnershipPanel } from './BikeOwnershipPanel';
import { useAssignBike, useBike, useBikes, useCreateBike, useReleaseBike } from './data';
import { useInvestorOptions } from './investorData';

/** The rider-facing names for who owns a bike. */
export const RIDER_BIKE_OWNERSHIP: Record<'rider' | 'cenohub' | 'investor' | 'other', string> = {
  rider: 'Self Bike',
  cenohub: 'Company Bike',
  investor: 'Investor Bike',
  other: 'Other',
};

export function AssignBikeDialog({ rider, onClose }: { rider: RiderDirectoryRow; onClose: () => void }): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const current = useBike(rider.bikeId);
  const register = useBikes();
  const assign = useAssignBike();
  const release = useReleaseBike();
  const create = useCreateBike();
  const investors = useInvestorOptions();

  const [bikeId, setBikeId] = useState('');
  const [from, setFrom] = useState(today);
  const [registration, setRegistration] = useState('');
  const [ownership, setOwnership] = useState<BikeOwnership | ''>('');
  const [investorId, setInvestorId] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const error = assign.error ?? release.error ?? create.error;
  const fields = error instanceof ApiError ? error.fields : undefined;
  const bike = current.data?.data;
  const others = (register.data?.bikes ?? []).filter((b) => b.id !== rider.bikeId && b.status !== 'sold');

  return (
    <Modal open onClose={onClose} title={`Bike for ${rider.canonicalName}`} size="lg">
      <div className="space-y-4">
        <section>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Their bike now</p>
          {!rider.bikeId ? (
            <p className="text-sm text-slate-600">Unassigned — they hold no bike on the register.</p>
          ) : !bike ? (
            <TableSkeleton rows={2} columns={2} />
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <strong>{bike.registration}</strong> ·{' '}
                  {bike.ownership === 'investor'
                    ? `Investor Bike${bike.investorName ? ` (${bike.investorName})` : ''}`
                    : RIDER_BIKE_OWNERSHIP[bike.ownership]}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={release.isPending}
                  onClick={() =>
                    release.mutate(
                      { id: bike.id, releasedOn: today },
                      { onSuccess: () => setNote(`${bike.registration} taken back from ${rider.canonicalName}.`) },
                    )
                  }
                >
                  Take back
                </Button>
              </div>
              <BikeOwnershipPanel
                bike={bike}
                currency={currency}
                mayChange={can('bikes:manage') && can('remittance:manage')}
                mayChangeShare={can('remittance:manage')}
              />
            </div>
          )}
        </section>

        <form
          aria-label="Assign a bike"
          className="space-y-2 rounded-lg border border-slate-200 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            assign.mutate(
              { id: bikeId, input: { riderId: rider.id, assignedOn: from } },
              {
                onSuccess: () => {
                  setNote(`Assigned ${others.find((b) => b.id === bikeId)?.registration ?? 'the bike'} from ${from}.`);
                  setBikeId('');
                },
              },
            );
          }}
        >
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {rider.bikeId ? 'Change to another bike' : 'Assign a bike from the register'}
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <Select
              label="Bike"
              value={bikeId}
              onChange={(e) => setBikeId(e.target.value)}
              placeholder={others.length ? 'Choose a bike…' : 'No other bike on the register'}
              options={others.map((b) => ({
                value: b.id,
                label: `${b.registration} · ${
                  b.ownership === 'investor' ? `Investor Bike${b.investorName ? ` (${b.investorName})` : ''}` : RIDER_BIKE_OWNERSHIP[b.ownership]
                }${b.riderName ? ` · with ${b.riderName}` : ''}`,
              }))}
              error={fields?.riderId?.[0]}
            />
            <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} error={fields?.assignedOn?.[0]} />
            <Button type="submit" disabled={!bikeId || assign.isPending}>
              Assign
            </Button>
          </div>
        </form>

        <form
          aria-label="Register a bike for this rider"
          className="space-y-2 rounded-lg border border-slate-200 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ownership) return;
            create.mutate(
              {
                registration,
                ownership,
                ...(ownership === 'investor' ? { investorId } : {}),
                riderId: rider.id,
                purchaseDate: from,
                category: 'motorcycle',
                status: 'active',
              },
              {
                onSuccess: () => {
                  setNote(`${registration} registered and assigned to ${rider.canonicalName}.`);
                  setRegistration('');
                  setOwnership('');
                  setInvestorId('');
                },
              },
            );
          }}
        >
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Register a new bike for them</p>
          <div className="grid gap-2 sm:grid-cols-3">
            <Input
              label="Registration"
              required
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
              error={fields?.registration?.[0]}
            />
            <Select
              label="Bike ownership"
              required
              value={ownership}
              onChange={(e) => setOwnership(e.target.value as BikeOwnership | '')}
              placeholder="Choose…"
              options={[
                { value: 'rider', label: 'Self Bike (rider-owned)' },
                { value: 'cenohub', label: 'Company Bike' },
                { value: 'investor', label: 'Investor Bike' },
              ]}
              error={fields?.ownership?.[0]}
              hint={
                ownership === 'rider'
                  ? 'No weekly remittance unless an individual agreement exists.'
                  : ownership === 'investor'
                    ? 'The investor gets their weekly share; the company the rest.'
                    : ownership === 'cenohub'
                      ? 'Remittance follows your rules; all of it is the company’s.'
                      : undefined
              }
            />
            {ownership === 'investor' ? (
              <Select
                label="Investor"
                required
                value={investorId}
                onChange={(e) => setInvestorId(e.target.value)}
                placeholder={(investors.data ?? []).length ? 'Choose the investor…' : 'Add one in Finance → Investors'}
                options={(investors.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
                error={fields?.investorId?.[0]}
              />
            ) : null}
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={create.isPending || !registration.trim() || !ownership || (ownership === 'investor' && !investorId)}
            >
              Register and assign
            </Button>
          </div>
        </form>

        {error && !fields ? <ErrorState message={error.message} /> : null}
        {note ? (
          <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            {note}
          </p>
        ) : null}

        <div className="flex justify-end border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
